<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
    <!-- Decorative Background -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute w-96 h-96 bg-violet-200/30 dark:bg-violet-900/20 rounded-full blur-3xl -top-48 -left-48"></div>
      <div class="absolute w-96 h-96 bg-blue-200/30 dark:bg-blue-900/20 rounded-full blur-3xl -bottom-48 -right-48"></div>
    </div>

    <!-- Login Card -->
    <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md z-10 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-violet-600 to-blue-600 p-6 text-center">
        <div class="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
        </div>
        <h1 class="text-2xl font-bold text-white">Myco</h1>
        <p class="text-violet-100 text-sm mt-1">Sistema de Gestión</p>
      </div>

      <!-- Form Section -->
      <div class="p-6">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Bienvenido 👋
        </h2>
        <p class="text-gray-500 dark:text-gray-400 text-sm mb-5">
          Ingresa tus credenciales para continuar
        </p>

        <form @submit.prevent="handleLogin" class="space-y-3">
          <BaseInput
            v-model="form.email"
            type="email"
            label="Email"
            placeholder="usuario@ejemplo.com"
            required
            size="sm"
          />

          <BaseInput
            v-model="form.password"
            type="password"
            label="Contraseña"
            placeholder="••••••••"
            required
            size="sm"
            :show-password-toggle="true"
          />

          <div v-if="error" class="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
            {{ error }}
          </div>

          <BaseButton
            type="submit"
            variant="primary"
            size="md"
            :loading="loading"
            class="w-full"
          >
            Iniciar Sesión
          </BaseButton>
        </form>

        <!-- Quick Login for Development -->
        <div class="mt-5 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-amber-600 dark:text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 class="text-xs font-semibold text-amber-800 dark:text-amber-300">
              Acceso Rápido (Solo Desarrollo)
            </h3>
          </div>

          <div class="space-y-1.5">
            <BaseButton
              variant="danger"
              size="xs"
              class="w-full justify-center"
              @click="quickLogin('nerk@example.com', 'nerkkren')"
            >
              <BaseBadge variant="danger" size="sm" class="mr-2">Super Admin</BaseBadge>
              nerk@example.com
            </BaseButton>

            <BaseButton
              variant="primary"
              size="xs"
              class="w-full justify-center"
              @click="quickLogin('admin@example.com', 'admin123')"
            >
              <BaseBadge variant="primary" size="sm" class="mr-2">Admin</BaseBadge>
              admin@example.com
            </BaseButton>

            <div class="grid grid-cols-3 gap-1.5">
              <BaseButton
                variant="secondary"
                size="xs"
                @click="quickLogin('rol1@example.com', 'rol1123')"
              >
                Rol 1
              </BaseButton>
              <BaseButton
                variant="success"
                size="xs"
                @click="quickLogin('rol2@example.com', 'rol2123')"
              >
                Rol 2
              </BaseButton>
              <BaseButton
                variant="warning"
                size="xs"
                @click="quickLogin('rol3@example.com', 'rol3123')"
              >
                Rol 3
              </BaseButton>
            </div>
          </div>

          <p class="text-xs text-amber-700 dark:text-amber-400 mt-2 flex items-center gap-1">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
            Remover en producción
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useHttp } from '@/composables/useHttp'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'

const router = useRouter()

const form = ref({
  email: '',
  password: '',
  remember: false
})
const { post, loading, error: httpError, data } = useHttp()
const error = ref('')
const userStore = useUserStore()

const handleLogin = async () => {
  error.value = ''
  try {
    await post('login', {
      email: form.value.email,
      password: form.value.password
      // remember: form.value.remember
    })
    console.log(data.value);
    // Guarda el token en localStorage
    localStorage.setItem('token', data.value.token)
    // Guarda usuario y negocios en el store
    userStore.setUserData(data.value)
    router.push('/')
  } catch (err) {
    console.log('err', err);
    console.log('httpError', httpError.value);
    error.value = httpError.value || 'Login failed. Please try again.'
  }
}

// Quick login function for development
const quickLogin = async (username, password) => {
  error.value = ''

  // Llenar los campos del formulario para que sea visible
  form.value.email = username
  form.value.password = password

  try {
    await post('login', {
      email: username, // Can be username or email
      password: password
    })
    // console.log('Quick login successful:', data.value);
    // Guarda el token en localStorage
    localStorage.setItem('token', data.value.token)
    // Guarda usuario y negocios en el store
    userStore.setUserData(data.value)
    router.push('/')
  } catch (err) {
    // console.log('Quick login error:', err);
    console.log('httpError', httpError.value);
    error.value = httpError.value || 'Quick login failed. Please try again.'
  }
}
</script>

<!-- Tailwind CSS is assumed to be set up in your project --> 